import { getImage, IGatsbyImageData } from 'gatsby-plugin-image'
import React from 'react'

import { Button } from '@icontainers/design-system/dist/molecules/Button'
import { BackgroundImage } from '@src/Application/Interfaces/Atoms/BackgroundImage/BackgroundImage'
import { ActionItemWithIcon } from '@src/Application/Interfaces/Features/Header/Nav/ActionItemWithIcon'
import { ArrowDirectionIcon } from '@src/Application/Interfaces/Icons'
import { DynamicColumnComponentType } from '@src/Application/Types/HeaderTypes'

import {
    BackgroundImageContent,
    BackgroundImageDescription,
    BackgroundImageLink,
    BackgroundImageTitle,
    BackgroundImageWrapper,
    BlueLink,
    Footer,
    FooterContent,
    FooterDescription,
    FooterImage,
    ImageDescription,
    ImageTitle,
    Link,
    Section,
    SectionTitle,
    TopImage,
    TopImageWrapper,
    Wrapper,
} from './DynamicColumnSubMenu.styled'

export const DynamicColumnSubMenu = (props: DynamicColumnComponentType): JSX.Element => {
    const {
        menuLinkSections,
        showFooter,
        footerImage,
        footerLinkLabel,
        footerLinkUrl,
        footerDescription,
        lastColumnImage,
        imagePosition = 'top',
        imageColumnTitle,
        ImageColumnDescription,
        imageColumnLinkLabel,
        imageColumnLinkUrl,
    } = props

    const backgroundImage =
        lastColumnImage && imagePosition === 'background' && (getImage(lastColumnImage.localFile) as IGatsbyImageData)

    const hasImageContent =
        Boolean(imageColumnTitle) ||
        Boolean(ImageColumnDescription) ||
        Boolean(imageColumnLinkUrl && imageColumnLinkLabel)

    const columnCount = (menuLinkSections ? menuLinkSections.length : 0) + (lastColumnImage ? 1 : 0)

    return (
        <>
            <Wrapper $columnCount={columnCount}>
                {menuLinkSections?.map((menu) => {
                    const isButtonType = menu.buttonStyle === 'button'

                    return (
                        <Section key={menu.title}>
                            <SectionTitle>{menu.title}</SectionTitle>
                            <>
                                {menu.link?.map((link) => (
                                    <ActionItemWithIcon
                                        name={link.name}
                                        description={link.description}
                                        icon={link.icon}
                                        url={link.url}
                                        tag={link.tag}
                                        key={`${menu.title}-menu-${link.name}`}
                                    />
                                ))}
                            </>
                            {menu.buttonUrl && menu.buttonLabel && isButtonType && (
                                <Link to={menu.buttonUrl}>
                                    <Button fullWidth colorStyle="secondary">
                                        {menu.buttonLabel}
                                    </Button>
                                </Link>
                            )}
                            {menu.buttonUrl && menu.buttonLabel && !isButtonType && (
                                <BlueLink to={menu.buttonUrl} $isViewAllLink>
                                    {menu.buttonLabel}
                                </BlueLink>
                            )}
                        </Section>
                    )
                })}
                {lastColumnImage && (
                    <Section $imageSection>
                        {imagePosition === 'background' && backgroundImage && (
                            <BackgroundImageWrapper>
                                <BackgroundImage image={backgroundImage} title={lastColumnImage.alternativeText}>
                                    <>
                                        {imageColumnTitle && (
                                            <BackgroundImageTitle>{imageColumnTitle}</BackgroundImageTitle>
                                        )}
                                        <BackgroundImageContent>
                                            {ImageColumnDescription && (
                                                <BackgroundImageDescription>
                                                    {ImageColumnDescription}
                                                </BackgroundImageDescription>
                                            )}
                                            {imageColumnLinkUrl && imageColumnLinkLabel && (
                                                <BackgroundImageLink to={imageColumnLinkUrl}>
                                                    {imageColumnLinkLabel}
                                                    <ArrowDirectionIcon />
                                                </BackgroundImageLink>
                                            )}
                                        </BackgroundImageContent>
                                    </>
                                </BackgroundImage>
                            </BackgroundImageWrapper>
                        )}
                        {imagePosition === 'top' && (
                            <TopImageWrapper $hasContent={hasImageContent}>
                                <TopImage image={lastColumnImage} />
                                {imageColumnTitle && <ImageTitle>{imageColumnTitle}</ImageTitle>}
                                {ImageColumnDescription && (
                                    <ImageDescription>{ImageColumnDescription}</ImageDescription>
                                )}
                                {imageColumnLinkUrl && imageColumnLinkLabel && (
                                    <BlueLink to={imageColumnLinkUrl}>{imageColumnLinkLabel}</BlueLink>
                                )}
                            </TopImageWrapper>
                        )}
                    </Section>
                )}
            </Wrapper>
            {showFooter && (
                <Footer>
                    {footerImage && <FooterImage image={footerImage} />}
                    <FooterContent>
                        {footerDescription && <FooterDescription>{footerDescription}</FooterDescription>}
                        {footerLinkLabel && footerLinkUrl && (
                            <BlueLink to={footerLinkUrl}>
                                {footerLinkLabel}
                                <ArrowDirectionIcon colorCode="p500" width={10} height={7} />
                            </BlueLink>
                        )}
                    </FooterContent>
                </Footer>
            )}
        </>
    )
}
